/**
 * The author's workspace: one review.
 *
 * ── The layout, and what the middle of it is ────────────────────────────────
 * The design brief settles a question this could have gone either way on. The
 * mockup's centre pane is labelled **VIEWING AS A LEARNER**, and both mockups
 * are built on `resources/export/style.css` — the reviewer one explicitly
 * "learner theme only, deliberately NOT the Qt frameless chrome". So the middle
 * is the *export*, not a re-drawing of the editor canvas. An author reviews what
 * will ship, and so does everyone they send it to.
 *
 * Three panes, filling the viewport with no page scroll: the outline on the
 * left, the course in the middle, the comment rail on the right. Each pane
 * scrolls on its own, which is what lets the rail track the course rather than
 * the page.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  ApiError,
  type ProjectTag,
  type ProjectWire,
  type ThreadsWire,
  type ThreadWire
} from '../api';
import { Link } from '../router';
import { Wordmark } from '../components/Wordmark';
import { mentionable, type Mentionable } from '../components/Composer';
import { ShareDialog } from '../components/ShareDialog';
import { Bell } from '../components/Bell';
import { AccountPresenceChip } from '../components/Presence';
import { ExportDialog, TagEditor } from '../components/ExportDialog';
import { CommentRail, type ComposeTarget } from '../components/rail/CommentRail';
import { useCourseBus, type CourseState } from '../components/rail/useCourseBus';
import { Avatar, type RailCaps } from '../components/rail/ThreadCard';
import { display, DUR, EASE, LAYOUT, mono, monoPlain, RADIUS } from '../theme';
import type { DiffWire, PulseWire } from '../api';

/** `7 Aug` — the mockup's due-date shape. */
const shortDate = (unix: number) => {
  const d = new Date(unix * 1000);
  return `${d.getDate()} ${d.toLocaleString('en-GB', { month: 'short' })}`;
};

/**
 * The workspace heartbeat: who is in, what is unread. Polls only while the
 * tab is visible — the same manners as Cloud's bell.
 */
function usePulse(projectId: string | null): PulseWire | null {
  const [pulse, setPulse] = useState<PulseWire | null>(null);

  useEffect(() => {
    if (!projectId) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let live = true;

    const tick = async () => {
      if (!live) return;
      if (document.visibilityState === 'visible') {
        try {
          setPulse(await api.pulse(projectId));
        } catch {
          // A missed beat is not news.
        }
      }
      timer = setTimeout(tick, 45_000);
    };
    void tick();

    const onVisible = () => {
      if (document.visibilityState === 'visible') void tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      live = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [projectId]);

  return pulse;
}

/**
 * Height guesses for the sweep, used only until the DOM has been measured —
 * the mockup's own constants for the author card.
 */
const AUTHOR_CAPS: Omit<RailCaps, 'canResolve' | 'resolveDisabledTitle'> = {
  theme: 'author',
  tagsInComposer: true,
  estimate: (t, collapsed) => (collapsed ? 60 : 86 + t.messages.length * 58),
  composerEstimate: 196
};

export function Project({ id }: { id: string }) {
  const [project, setProject] = useState<ProjectWire | null>(null);
  const [threads, setThreads] = useState<ThreadsWire | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [composeFor, setComposeFor] = useState<ComposeTarget | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [filter, setFilter] = useState<'open' | 'resolved' | 'all'>('open');
  const [query, setQuery] = useState('');
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [reviewerFilter, setReviewerFilter] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [tab, setTab] = useState<'outline' | 'comments'>('outline');
  const [sharing, setSharing] = useState(false);
  /**
   * Presenting hides the rail and the outline so the author can walk a client
   * through the course on a call. Client-side only — the course itself is
   * unchanged, and the same MVR_REVIEW flag that opens the gates still applies,
   * because someone presenting should not be stopped by a quiz either.
   */
  const [presenting, setPresenting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [editingTags, setEditingTags] = useState(false);
  const frame = useRef<HTMLIFrameElement | null>(null);

  const reload = useCallback(async () => {
    try {
      setThreads(await api.threads.list(id));
    } catch {
      // A failed refresh leaves the rail as it was rather than emptying it.
    }
  }, [id]);

  useEffect(() => {
    let live = true;
    api.projects
      .get(id)
      .then((p) => {
        if (!live) return;
        setProject(p);
        void reload();
      })
      .catch(
        (e) => live && setError(e instanceof ApiError ? e.message : 'That review would not load.')
      );
    return () => {
      live = false;
    };
  }, [id, reload]);

  const onComment = useCallback(
    (target: ComposeTarget) => {
      setComposeFor(target);
      setSelected(target.blockId);
      // Starting a comment while only resolved threads are showing would post
      // it into invisibility — the mockup flips the filter back.
      setFilter((f) => (f === 'resolved' ? 'all' : f));
    },
    []
  );


  // What changed since the previous round: the outline's dots, the course's
  // violet edges, and the ROUNDS card all read from this one fetch.
  const [sinceLast, setSinceLast] = useState<DiffWire | null>(null);
  useEffect(() => {
    if (!project || project.round < 2) return;
    api.rounds
      .diff(project.id, project.round - 1, project.round)
      .then(setSinceLast)
      .catch(() => undefined);
  }, [project]);

  const changedIds = useMemo(
    () =>
      (sinceLast?.changes ?? [])
        .filter((ch) => ch.kind === 'edited' || ch.kind === 'added')
        .map((ch) => ch.blockId),
    [sinceLast]
  );

  const { course, sendSelect } = useCourseBus({
    frame,
    threads: threads?.threads ?? null,
    changed: changedIds,
    onComment,
    onSelect: setSelected
  });

  const select = useCallback(
    (blockId: string) => {
      setSelected(blockId);
      sendSelect(blockId);
    },
    [sendSelect]
  );

  const people = useMemo<Mentionable[]>(
    () => (threads ? mentionable(threads.reviewers) : []),
    [threads]
  );

  const caps = useMemo<RailCaps>(
    () => ({
      ...AUTHOR_CAPS,
      canResolve: () => !!threads?.me.isOwner,
      resolveDisabledTitle: 'Only the person who sent this review can resolve'
    }),
    [threads?.me.isOwner]
  );

  const tags: ProjectTag[] = project?.tags ?? [];
  const pulse = usePulse(project?.id ?? null);


  if (error) {
    return (
      <Frame>
        <div style={{ padding: '80px 32px', textAlign: 'center' }}>
          <h1 style={display(26)}>{error}</h1>
          <p style={{ marginTop: 12 }}>
            <Link to="/">Back to your reviews</Link>
          </p>
        </div>
      </Frame>
    );
  }

  if (!project) {
    return (
      <Frame>
        <div style={{ padding: '80px 32px', textAlign: 'center', ...mono }}>Loading</div>
      </Frame>
    );
  }

  const nameFor = (key: string) => people.find((p) => p.key === key)?.name ?? '';
  const visible = (threads?.threads ?? []).filter((t) => {
    if (filter === 'open' && t.resolvedAt) return false;
    if (filter === 'resolved' && !t.resolvedAt) return false;
    if (tagFilter && t.tagId !== tagFilter) return false;
    if (reviewerFilter && !t.messages.some((m) => m.authorKey === reviewerFilter)) return false;
    const q = query.trim().toLowerCase();
    if (q) {
      // Names and bodies together, across the whole thread — the search is
      // "find what Priya said", not "find this exact string in one message".
      const hay = t.messages.map((m) => `${nameFor(m.authorKey)} ${m.body}`).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const openCount = (threads?.threads ?? []).filter((t) => !t.resolvedAt).length;
  const reviewerCount = (threads?.reviewers ?? []).filter((r) => !r.isOwner).length;

  return (
    <Frame
      title={project.title}
      meta={`ROUND ${project.round}${project.dueAt ? ` · DUE ${shortDate(project.dueAt).toUpperCase()}` : ''}`}
      live={pulse?.reviewersIn ?? 0}
      headerExtra={
        <>
          <Bell />
          {/* The one account chip in this app. The other two author screens —
              ProjectList and Rounds — have no chip at all, and `Frame` is
              private to this file, so the status dot lives here and nowhere
              else. `threads.me.name` is the actor's display name; the account
              behind it is `useApp().me`, which the socket in state.tsx keys on. */}
          {threads?.me && <AccountPresenceChip name={threads.me.name} colour="#b5abfc" />}
        </>
      }
      onShare={() => setSharing(true)}
      onPresent={() => setPresenting(true)}
      onExport={() => setExporting(true)}
      presenting={presenting}
      onExitPresent={() => setPresenting(false)}
    >
      {sharing && <ShareDialog projectId={project.id} onClose={() => setSharing(false)} />}
      {exporting && (
        <ExportDialog
          projectId={project.id}
          title={project.title}
          threads={threads?.threads ?? []}
          people={people}
          tags={tags}
          onClose={() => setExporting(false)}
        />
      )}
      {editingTags && (
        <TagEditor
          projectId={project.id}
          tags={tags}
          onClose={() => setEditingTags(false)}
          onSaved={async () => {
            setEditingTags(false);
            setProject(await api.projects.get(project.id));
          }}
        />
      )}
      <div
        style={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          gap: presenting ? 0 : LAYOUT.gutter
        }}
      >
        {!presenting && (
          <Outline
            lessons={course.lessons}
            current={course.lesson}
            boxes={course.boxes}
            threads={threads?.threads ?? []}
            people={people}
            tags={tags}
            missingMedia={project.missingMedia}
            tab={tab}
            onTab={setTab}
            selected={selected}
            onSelectBlock={select}
            round={project.round}
            changed={changedIds}
            changedCount={
              sinceLast ? sinceLast.stats.edited + sinceLast.stats.added : 0
            }
          />
        )}

        {/* The course canvas. `overflow: hidden` is what clips the iframe to the
            panel's corners — the document inside it is Compose's runtime on
            `preview.mauvely.com` and nothing here reaches into it. */}
        <main
          style={{
            flex: 1,
            minWidth: 0,
            position: 'relative',
            background: 'var(--app-panel)',
            borderRadius: presenting ? 0 : RADIUS.appPanel,
            overflow: 'hidden'
          }}
        >
          {project.state === 'live' ? (
            <CourseFrame projectId={project.id} innerRef={frame} />
          ) : (
            <div style={{ padding: 40, textAlign: 'center', ...mono }}>
              This course is still uploading
            </div>
          )}
        </main>

        {!presenting && (
          /* An inspector is a content region, so it is a neutral `--app-panel`
             rather than the tinted chrome the outline rail wears. */
          <aside
            style={{
              width: LAYOUT.rail,
              flex: 'none',
              background: 'var(--app-panel)',
              borderRadius: RADIUS.appPanel,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0
            }}
          >
            <CommentRail
              projectId={project.id}
              wire={threads}
              visible={visible}
              people={people}
              tags={tags}
              caps={caps}
              course={course}
              frameRef={frame}
              selected={selected}
              composeFor={composeFor}
              filter={filter}
              query={query}
              header={
                <RailHeader
                  filter={filter}
                  openCount={openCount}
                  resolvedCount={(threads?.threads.length ?? 0) - openCount}
                  panelOpen={panelOpen}
                  query={query}
                  tags={tags}
                  tagFilter={tagFilter}
                  reviewers={(threads?.reviewers ?? [])
                    .filter((r) => !r.isOwner)
                    .map((r) => ({ key: r.key, name: r.name, colour: r.colour }))}
                  reviewerFilter={reviewerFilter}
                  onFilter={setFilter}
                  onTogglePanel={() => setPanelOpen((o) => !o)}
                  onQuery={setQuery}
                  onTagFilter={setTagFilter}
                  onReviewerFilter={setReviewerFilter}
                  onEditTags={() => setEditingTags(true)}
                />
              }
              onCompose={setComposeFor}
              onSelect={select}
              onChanged={reload}
              composerPlaceholder="What needs changing? @ to tag someone."
              composerSubmitLabel="Comment"
              overallAddLabel="Add an overall comment"
            />
          </aside>
        )}
      </div>

      <footer
        style={{
          height: LAYOUT.statusStrip,
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '0 8px'
        }}
      >
        <span style={mono}>
          {project.slug} · round {project.round}
        </span>
        <span style={{ flex: 1 }} />
        {course.lesson >= 0 && course.lessons.length > 0 && (
          <span style={mono}>
            Lesson {course.lesson + 1} / {course.lessons.length}
          </span>
        )}
        <span style={mono}>
          {openCount} open · {(threads?.threads.length ?? 0) - openCount} resolved
        </span>
        {reviewerCount > 0 && (
          <span style={mono}>
            {reviewerCount} reviewer{reviewerCount === 1 ? '' : 's'}
          </span>
        )}
      </footer>
    </Frame>
  );
}

// ── panes ───────────────────────────────────────────────────────────────────

function Outline({
  lessons,
  current,
  boxes,
  threads,
  people,
  tags,
  missingMedia,
  tab,
  onTab,
  selected,
  onSelectBlock,
  round,
  changed,
  changedCount
}: {
  lessons: CourseState['lessons'];
  current: number;
  boxes: CourseState['boxes'];
  threads: ThreadWire[];
  people: Mentionable[];
  tags: ProjectTag[];
  missingMedia: string[];
  tab: 'outline' | 'comments';
  onTab: (t: 'outline' | 'comments') => void;
  selected: string | null;
  onSelectBlock: (blockId: string) => void;
  round: number;
  changed: string[];
  changedCount: number;
}) {
  const countFor = new Map<string, number>();
  for (const t of threads) {
    if (t.blockId) countFor.set(t.blockId, (countFor.get(t.blockId) ?? 0) + 1);
  }

  /**
   * Comments per lesson, matched through the frozen anchor label.
   *
   * A thread knows its block, and the runtime only keeps the current lesson's
   * blocks in the DOM — so for every other lesson the label's leading segment
   * (the lesson title, uppercased at creation) is the one mapping we still
   * have. It survives renames badly and deletions fine, which is the right
   * trade for a count.
   */
  const lessonCount = (title: string) =>
    threads.filter((t) => t.anchorLabel?.startsWith(`${title.toUpperCase()} \u00b7`)).length;

  const openThreads = threads.filter((t) => !t.resolvedAt);

  /**
   * The tab underline, drawn once and slid between the two tabs rather than
   * painted on whichever one is active.
   *
   * Measured rather than computed: the two labels are different lengths and the
   * comments one carries a count that changes while the page is open, so there
   * is no arithmetic that stays true. `offsetLeft` and `offsetWidth` are read
   * against the strip, which is why the strip is `position: relative`.
   */
  const strip = useRef<HTMLDivElement>(null);
  const [marker, setMarker] = useState<{ left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    const el = strip.current?.querySelector<HTMLElement>('[data-tab-active="true"]');
    setMarker(el ? { left: el.offsetLeft, width: el.offsetWidth } : null);
  }, [tab, threads.length]);

  return (
    // Navigation, so the tinted chrome panel rather than a neutral content one.
    <aside
      style={{
        width: LAYOUT.sidebar,
        flex: 'none',
        background: 'var(--app-chrome-panel)',
        borderRadius: RADIUS.appPanel,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0
      }}
    >
      <div
        ref={strip}
        style={{
          position: 'relative',
          display: 'flex',
          gap: 2,
          padding: '0 10px',
          borderBottom: '1px solid var(--border-subtle)',
          flex: 'none'
        }}
      >
        {(
          [
            ['outline', 'Outline'],
            ['comments', `Comments \u00b7 ${threads.length}`]
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            data-tab-active={tab === key}
            onClick={() => onTab(key)}
            style={{
              ...mono,
              fontSize: 9.5,
              padding: '11px 8px 9px',
              background: 'none',
              border: 0,
              cursor: 'pointer',
              color: tab === key ? 'var(--text-strong)' : 'var(--text-muted)'
            }}
          >
            {label}
          </button>
        ))}
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 0,
            bottom: -1,
            height: 2,
            width: marker?.width ?? 0,
            background: 'var(--primary)',
            transform: `translateX(${marker?.left ?? 0}px)`,
            opacity: marker ? 1 : 0,
            transition: `transform ${DUR.base}ms ${EASE}, width ${DUR.base}ms ${EASE}`,
            pointerEvents: 'none'
          }}
        />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 0' }}>
        {/*
          Before the lesson list, because it has to be seen before a link goes
          out. Compose keeps plain disk paths valid for media — it resolves
          them from the filesystem when it packs — and Review has no
          filesystem, so those blocks arrive with nothing behind them.
        */}
        {missingMedia.length > 0 && (
          <div
            style={{
              margin: '0 12px 12px',
              padding: '10px 12px',
              borderRadius: RADIUS.control,
              border: '1px solid var(--danger-500)',
              color: 'var(--danger-500)',
              fontSize: 12,
              lineHeight: 1.5
            }}
          >
            {missingMedia.length} block{missingMedia.length === 1 ? '' : 's'} lost their media on
            the way here. Re-add in Compose and send again.
          </div>
        )}

        {tab === 'outline' && (
          <>
            {changedCount > 0 && (
              <p
                style={{
                  ...mono,
                  fontSize: 8.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '0 16px 8px'
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 999,
                    border: '2px solid var(--primary)',
                    flex: 'none'
                  }}
                />
                {changedCount} BLOCK{changedCount === 1 ? '' : 'S'} EDITED SINCE ROUND {round - 1}
              </p>
            )}
            {lessons.length === 0 && (
              <p style={{ ...monoPlain, padding: '0 16px' }}>Waiting for the course</p>
            )}

            {lessons.map((l) => {
              const n = lessonCount(l.title);
              const active = l.index === current;
              return (
                <div key={l.index}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 16px',
                      fontSize: 13,
                      color: active ? 'var(--text-strong)' : 'var(--text-muted)',
                      background: active ? 'var(--primary-soft)' : 'transparent'
                    }}
                  >
                    <span style={{ ...mono, minWidth: 14 }}>{l.index + 1}</span>
                    <span
                      style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {l.title}
                    </span>
                    {n > 0 && <span style={{ ...monoPlain, fontSize: 9 }}>{n}</span>}
                  </div>

                  {/* The block sub-tree, for the lesson being read — the only
                      one whose blocks the runtime has in the DOM. */}
                  {active && boxes.length > 0 && (
                    <div
                      style={{
                        margin: '2px 0 6px 22px',
                        borderLeft: '1px solid var(--primary-soft)'
                      }}
                    >
                      {boxes.map((b, i) => {
                        const bn = countFor.get(b.id) ?? 0;
                        const hasOpen = threads.some(
                          (t) => t.blockId === b.id && !t.resolvedAt
                        );
                        return (
                          <button
                            key={b.id}
                            type="button"
                            className="mv-row"
                            onClick={() => onSelectBlock(b.id)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              width: '100%',
                              padding: '5px 14px 5px 12px',
                              background:
                                selected === b.id ? 'var(--primary-soft)' : 'none',
                              border: 0,
                              cursor: 'pointer',
                              textAlign: 'left',
                              fontSize: 12,
                              color: 'var(--text-muted)'
                            }}
                          >
                            <span
                              style={{
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {b.label || `Block ${i + 1}`}
                            </span>
                            {changed.includes(b.id) && (
                              <span
                                aria-hidden="true"
                                title="Edited since the last round"
                                style={{
                                  width: 7,
                                  height: 7,
                                  borderRadius: 999,
                                  border: '2px solid var(--primary)',
                                  flex: 'none'
                                }}
                              />
                            )}
                            {hasOpen && (
                              <span
                                aria-hidden="true"
                                style={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: 999,
                                  background: 'var(--primary)',
                                  flex: 'none'
                                }}
                              />
                            )}
                            {bn > 0 && <span style={{ ...monoPlain, fontSize: 9 }}>{bn}</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {openThreads.length > 0 && (
              <p style={{ ...monoPlain, padding: '12px 16px 0' }}>
                {openThreads.length} open thread{openThreads.length === 1 ? '' : 's'}
              </p>
            )}

            {round > 1 && (
              <div
                style={{
                  margin: '14px 12px 0',
                  padding: '10px 12px',
                  borderRadius: RADIUS.control,
                  border: '1px solid var(--border-subtle)'
                }}
              >
                <p style={{ ...mono, fontSize: 8.5, margin: 0 }}>Rounds</p>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '6px 0 8px', lineHeight: 1.5 }}>
                  Round {round - 1} is closed.
                  {changedCount > 0
                    ? ` You edited ${changedCount} block${changedCount === 1 ? '' : 's'} since.`
                    : ''}
                </p>
                <Link
                  to={`${window.location.pathname}/rounds`}
                  style={{ ...mono, fontSize: 9, color: 'var(--primary)' }}
                >
                  COMPARE ROUNDS
                </Link>
              </div>
            )}
          </>
        )}

        {tab === 'comments' && (
          <>
            {/* A JSX text node, so the separator is the character rather than an
                escape — the six characters of `\u00b7` were on screen. */}
            <p style={{ ...mono, padding: '0 16px 8px' }}>All comments · {threads.length}</p>
            {threads.length === 0 && (
              <p style={{ ...monoPlain, padding: '0 16px' }}>Nothing here yet.</p>
            )}
            {threads.map((t) => {
              const first = t.messages[0];
              const author = first ? people.find((p) => p.key === first.authorKey) : undefined;
              const tag = tags.find((x) => x.id === t.tagId);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => t.blockId && onSelectBlock(t.blockId)}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 16px',
                    background: 'none',
                    border: 0,
                    borderBottom: '1px solid var(--border-subtle)',
                    cursor: t.blockId ? 'pointer' : 'default',
                    textAlign: 'left'
                  }}
                >
                  <span
                    style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}
                  >
                    {author && <Avatar name={author.name} colour={author.colour} size={14} />}
                    <span style={{ ...monoPlain, fontSize: 8.5, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.scope === 'overall' ? 'OVERALL \u00b7 WHOLE COURSE' : t.anchorLabel}
                    </span>
                    <span
                      style={{
                        ...mono,
                        fontSize: 8,
                        color: t.resolvedAt ? 'var(--signal)' : 'var(--primary)'
                      }}
                    >
                      {t.resolvedAt ? 'RESOLVED' : 'OPEN'}
                    </span>
                  </span>
                  <span
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      fontSize: 12,
                      lineHeight: 1.45,
                      color: 'var(--text-muted)'
                    }}
                  >
                    {tag ? `[${tag.label}] ` : ''}
                    {first?.body ?? ''}
                  </span>
                </button>
              );
            })}
          </>
        )}
      </div>
    </aside>
  );
}

/**
 * The course, framed.
 *
 * Three things here are load-bearing rather than incidental:
 *
 *   · **It points at the preview origin, never at this one.** A course runs
 *     author-written script; on this origin that would sit beside the author's
 *     session.
 *   · **`sandbox` has no `allow-same-origin`.** The document gets an opaque
 *     origin — no cookie, no storage, no reach into this page. The Worker sends
 *     the CSP `sandbox` directive too, which covers a direct navigation. Adding
 *     `allow-same-origin` here would silently undo both.
 *   · **It fills its pane and scrolls internally.** The runtime's own chrome is
 *     `position: fixed`, so sizing it to its content would detach the top bar
 *     and lesson bar from the reader.
 */
function CourseFrame({
  projectId,
  innerRef
}: {
  projectId: string;
  innerRef: React.MutableRefObject<HTMLIFrameElement | null>;
}) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      fetch('/api/review/config').then((r) => r.json() as Promise<{ previewOrigin?: string }>),
      api.preview(projectId)
    ])
      .then(([c, p]) => setSrc(c.previewOrigin ? `${c.previewOrigin}${p.path}` : null))
      .catch(() => setSrc(null));
  }, [projectId]);

  if (!src) return <div style={{ padding: 40, ...monoPlain }}>Loading the course</div>;

  return (
    <iframe
      ref={innerRef}
      title="The course, as a learner sees it"
      src={src}
      sandbox="allow-scripts allow-forms allow-popups"
      style={{ display: 'block', width: '100%', height: '100%', border: 0 }}
    />
  );
}

/** The rail's toolbar: title, tag editor entry, filter pills, the funnel —
 *  and, open, the panel with search and the tag and reviewer filters. */
function RailHeader({
  filter,
  openCount,
  resolvedCount,
  panelOpen,
  query,
  tags,
  tagFilter,
  reviewers,
  reviewerFilter,
  onFilter,
  onTogglePanel,
  onQuery,
  onTagFilter,
  onReviewerFilter,
  onEditTags
}: {
  filter: 'open' | 'resolved' | 'all';
  openCount: number;
  resolvedCount: number;
  panelOpen: boolean;
  query: string;
  tags: ProjectTag[];
  tagFilter: string | null;
  reviewers: Mentionable[];
  reviewerFilter: string | null;
  onFilter: (f: 'open' | 'resolved' | 'all') => void;
  onTogglePanel: () => void;
  onQuery: (q: string) => void;
  onTagFilter: (id: string | null) => void;
  onReviewerFilter: (key: string | null) => void;
  onEditTags: () => void;
}) {
  const filtering = !!query.trim() || tagFilter !== null || reviewerFilter !== null;

  return (
    // A toolbar inside a panel, so `--app-panel-alt`: one step off the panel it
    // sits on, in the same neutral family. The divider under it goes with it.
    <div style={{ flex: 'none', background: 'var(--app-panel-alt)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 12px' }}>
        <span style={mono}>Comments</span>
        <button
          type="button"
          onClick={onEditTags}
          title="Edit the tag set for this review"
          style={{ ...mono, fontSize: 9, background: 'none', border: 0, color: 'var(--text-muted)', cursor: 'pointer' }}
        >
          TAGS
        </button>
        <span style={{ flex: 1 }} />
        {(['open', 'resolved', 'all'] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => onFilter(f)}
            style={{
              ...mono,
              fontSize: 9.5,
              padding: '4px 8px',
              borderRadius: RADIUS.chip,
              cursor: 'pointer',
              border: `1px solid ${filter === f ? 'var(--primary)' : 'var(--border-subtle)'}`,
              background: filter === f ? 'var(--primary-soft)' : 'transparent',
              color: filter === f ? 'var(--primary)' : 'var(--text-muted)'
            }}
          >
            {f === 'open' ? `OPEN · ${openCount}` : f === 'resolved' ? `RESOLVED · ${resolvedCount}` : 'ALL'}
          </button>
        ))}
        <button
          type="button"
          onClick={onTogglePanel}
          title="Filter comments"
          aria-expanded={panelOpen}
          style={{
            ...mono,
            fontSize: 11,
            padding: '3px 6px',
            borderRadius: RADIUS.chip,
            cursor: 'pointer',
            border: `1px solid ${panelOpen || filtering ? 'var(--primary)' : 'transparent'}`,
            background: 'none',
            color: panelOpen || filtering ? 'var(--primary)' : 'var(--text-muted)'
          }}
        >
          ⌕
        </button>
      </div>

      {panelOpen && (
        <div style={{ padding: '0 12px 12px', display: 'grid', gap: 10 }}>
          <input
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Search comments"
            style={{
              width: '100%',
              padding: '7px 10px',
              borderRadius: RADIUS.control,
              border: '1px solid var(--border-strong, var(--border-subtle))',
              background: 'var(--surface-sunken, transparent)',
              color: 'var(--text-strong)',
              font: 'inherit',
              fontSize: 12.5
            }}
          />

          {tags.length > 0 && (
            <div>
              <p style={{ ...mono, fontSize: 8.5, margin: '0 0 6px' }}>Tag · editable per course</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {tags.map((t) => {
                  const on = tagFilter === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => onTagFilter(on ? null : t.id)}
                      style={{
                        ...mono,
                        fontSize: 8.5,
                        padding: '3px 8px',
                        borderRadius: RADIUS.chip,
                        cursor: 'pointer',
                        color: t.colour,
                        background: on ? 'var(--primary-soft)' : 'none',
                        border: `1px solid ${on ? 'var(--primary)' : 'var(--border-subtle)'}`
                      }}
                    >
                      {t.label.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {reviewers.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {reviewers.map((r) => {
                const on = reviewerFilter === r.key;
                return (
                  <button
                    key={r.key}
                    type="button"
                    title={r.name}
                    onClick={() => onReviewerFilter(on ? null : r.key)}
                    style={{
                      background: 'none',
                      border: 0,
                      padding: 0,
                      cursor: 'pointer',
                      borderRadius: 999,
                      boxShadow: on ? '0 0 0 2px var(--primary)' : 'none',
                      opacity: reviewerFilter && !on ? 0.4 : 1
                    }}
                  >
                    <Avatar name={r.name} colour={r.colour} size={22} />
                  </button>
                );
              })}
              <span style={{ ...monoPlain, fontSize: 9, flex: 1 }}>
                {reviewerFilter ? 'Showing one reviewer' : 'All reviewers · tap an avatar to filter'}
              </span>
              {filtering && (
                <button
                  type="button"
                  onClick={() => {
                    onQuery('');
                    onTagFilter(null);
                    onReviewerFilter(null);
                  }}
                  style={{ ...mono, fontSize: 9, background: 'none', border: 0, color: 'var(--primary)', cursor: 'pointer' }}
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** The dark author chrome from the mockup, minus the native menu bar. */
function Frame({
  title,
  meta,
  live,
  headerExtra,
  onShare,
  onPresent,
  onExport,
  presenting,
  onExitPresent,
  children
}: {
  title?: string;
  meta?: string;
  /** Reviewers in right now, for the teal LIVE pill. */
  live?: number;
  /** Bell, user chip — whatever else belongs at the right edge. */
  headerExtra?: React.ReactNode;
  onShare?: () => void;
  onPresent?: () => void;
  onExport?: () => void;
  presenting?: boolean;
  onExitPresent?: () => void;
  children: React.ReactNode;
}) {
  const G = LAYOUT.gutter;

  return (
    /*
     * Tinted chrome on a panel layout, decided 2026-09-09 and already shipping
     * in Snap and Suite. The window ground is violet (`--app-ground`); the
     * outline rail is a panel of its own on it, one step lighter and still
     * tinted; the course canvas and the comment inspector are neutral panels;
     * and `LAYOUT.gutter` of ground shows between them and around the outside.
     * The bar and the status strip sit on that ground with nothing under them —
     * the dividers they used to draw are what made the window read as one flat
     * grey.
     *
     * The gutter is this container's padding rather than the bar's, which is
     * safe here and is not in the scrolling apps: nothing scrolls under the bar
     * in a `height: 100vh; overflow: hidden` window, so it does not have to be
     * sticky.
     */
    <div
      className="mv-app"
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        gap: presenting ? 0 : G,
        padding: presenting ? 0 : G,
        background: 'var(--app-ground)',
        overflow: 'hidden'
      }}
    >
      {presenting && (
        // The mockup's floating pill, bottom-centre: chrome is gone, and the
        // one control left is the way back.
        <div
          style={{
            position: 'fixed',
            left: '50%',
            bottom: 22,
            transform: 'translateX(-50%)',
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '9px 16px',
            borderRadius: RADIUS.pill,
            background: 'rgba(22,24,38,.86)',
            backdropFilter: 'blur(10px)',
            border: '1px solid var(--border-subtle)'
          }}
        >
          <span
            aria-hidden="true"
            style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--signal)' }}
          />
          <span style={{ ...mono, color: 'var(--text-muted)' }}>Presenting · comments hidden</span>
          <button
            type="button"
            onClick={onExitPresent}
            style={{ ...mono, background: 'none', border: 0, color: 'var(--primary)', cursor: 'pointer' }}
          >
            Exit
          </button>
        </div>
      )}
      <header
        style={{
          height: LAYOUT.topbar,
          flex: 'none',
          display: presenting ? 'none' : 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '0 8px'
        }}
      >
        <Link to="/">
          <Wordmark reversed size={15} />
        </Link>
        {title && (
          <>
            <span style={{ width: 1, height: 18, background: 'var(--border-subtle)' }} />
            <span style={{ color: 'var(--text-strong)', fontSize: 13 }}>{title}</span>
          </>
        )}
        <span style={{ flex: 1 }} />
        {typeof live === 'number' && live > 0 && (
          <span
            style={{
              ...mono,
              fontSize: 9,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 9px',
              borderRadius: RADIUS.pill,
              color: 'var(--signal)',
              border: '1px solid var(--signal)'
            }}
          >
            <span
              aria-hidden="true"
              style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--signal)' }}
            />
            LIVE · {live} REVIEWER{live === 1 ? '' : 'S'} IN
          </span>
        )}
        {meta && (
          <span
            style={{
              ...mono,
              fontSize: 9,
              padding: '4px 9px',
              borderRadius: RADIUS.pill,
              border: '1px solid var(--border-subtle)'
            }}
          >
            {meta}
          </span>
        )}
        {onPresent && (
          <button
            type="button"
            onClick={onPresent}
            style={{ ...mono, fontSize: 10, background: 'none', border: 0, color: 'var(--text-muted)' }}
          >
            PRESENT
          </button>
        )}
        {onExport && (
          <button
            type="button"
            onClick={onExport}
            style={{ ...mono, fontSize: 10, background: 'none', border: 0, color: 'var(--text-muted)' }}
          >
            EXPORT
          </button>
        )}
        {onShare && (
          <button
            type="button"
            onClick={onShare}
            style={{
              ...mono,
              fontSize: 10,
              padding: '5px 11px',
              borderRadius: RADIUS.chip,
              border: '1px solid var(--primary)',
              background: 'var(--primary-soft)',
              color: 'var(--primary)'
            }}
          >
            SHARE
          </button>
        )}
        {headerExtra}
      </header>
      {children}
    </div>
  );
}
